"use client";
import * as Dialog from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "framer-motion";
import { FaBars, FaTimes } from "react-icons/fa";
import { ReactNode, useState } from "react";
import { Button } from "./forms/button";

type CustomSheetProps = {
  children: ReactNode | ((closeMenu: () => void) => ReactNode);
};

export function DialogCustomSheet({ children }: CustomSheetProps) {
  const [open, setOpen] = useState(false);

  const closeMenu = () => setOpen(false);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <Button variant="none" aria-label="Open menu" className="p-1.5 hover:bg-gray-100 rounded-full transition-colors">
          <FaBars className="h-5 w-5 text-gray-700" />
        </Button>
      </Dialog.Trigger>

      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild forceMount>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30"
                onClick={() => setOpen(false)}
              />
            </Dialog.Overlay>

            <Dialog.Content asChild forceMount>
              <motion.div
                initial={{ y: -20, opacity: 0, scale: 0.95 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                exit={{ y: -20, opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, ease: [0.4, 0.0, 0.2, 1] }}
                className="fixed top-38 left-4 right-4 z-40 bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 max-w-sm mx-auto"
                style={{ transformOrigin: 'top center' }}
              >
                {/* Connection indicator - small triangle pointing to navbar */}
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                  <div className="w-4 h-4 bg-white/95 backdrop-blur-xl border-l border-t border-gray-200/50 rotate-45"></div>
                </div>
                <Dialog.Title className="sr-only">Navigation Menu</Dialog.Title>
                <Dialog.Description className="sr-only">
                  Mobile navigation menu with links and actions.
                </Dialog.Description>
                
                {/* Header */}
                <div className="flex items-center justify-between px-5 pt-5 pb-3">
                  {/* <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-brand-500 rounded-full animate-pulse"></div>
                    <span className="text-lg font-semibold text-gray-900">Menu</span>
                  </div> */}
                  
                  <Dialog.Close asChild>
                    <button 
                      className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                      aria-label="Close menu"
                    >
                      <FaTimes className="h-4 w-4 text-gray-600" />
                    </button>
                  </Dialog.Close>
                </div>

                {/* Content */}
                <div className="px-5 pb-5">
                  {typeof children === 'function' ? children(closeMenu) : children}
                </div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
